// ============================================
// Tutorial Tips — Progressive Onboarding
// ============================================
// Contextual tooltips shown once per trigger. Can be fired by:
//  1. Game events (energy choice, stat change, etc.) — Day <= 3 gate
//  2. Ink #tutorial:key tags — no Day gate (author-controlled)
// Tracked in localStorage. One at a time, auto-dismiss after 8s.
// Tips can optionally anchor to a UI element with a pulsing highlight.

import { events, Events } from '../engine/events.js';
import { storyManager } from '../engine/story.js';
import { variableManager } from '../engine/variables.js';
import { L } from '../systems/localization.js';
import { isPat } from '../engine/pgate.js';
import { contentMenuEnabled, hasVisibleContentTracks } from './content-tracks.js';

/** Tip metadata: optional CSS selector for anchor element */
const TUTORIAL_ANCHORS = {
    first_energy_choice: '#bar-energy',
    first_stat_change:   '#bar-fem',
    first_phone:         '[data-action="phone"]',
    first_shop:          null,
    first_night:         '#time-slots',
    day2_routine:        null,
    computer_intro:      null,
    first_arousal_high:  '#bar-energy',
    // Ink-triggered tips:
    wardrobe_intro:      '[data-action="wardrobe"]',
    first_outside:       null,
    first_sms:           '[data-action="phone"]',
    map_intro:           null,
    streaming_intro:     null,
    // v0.2.6 additions
    weekly_calendar:     '#week-strip',
    endgame_progress:    '[data-action="endgame-progress"]',
    first_achievement:   '.achievement-toast',
    // v0.2.8 additions
    paperdoll_intro:     '#paper-doll-panel',
    cloud_save:          '#pause-cloudsync',
    // v0.3 — points existing (save-game) players at the opt-in they'd otherwise
    // only meet in the character editor.
    content_prefs:       '#btn-settings',
};

/** Resolve hint text through L10n, falling back to English. */
function getTutorialText(id) {
    return L.get('tutorial.' + id);
}

const TUTORIAL_STORAGE_KEY = 'PnO_TutorialSeen';
const SESSION_START = Date.now();
const STARTUP_GRACE_MS = 5000; // Don't fire tutorial tips in the first 5s of session
let _tipQueue = [];
let _tipActive = false;
let _tipSeenThisSession = 0;
// Set on LOAD_COMPLETED (fires before the story is restored), consumed on the
// first following STORY_CHOICES_READY (story guaranteed ready) — content_prefs tip.
let _pendingContentPrefsTip = false;

/** Load the set of already-seen hint IDs from localStorage. */
function _getSeenHints() {
    try {
        const raw = localStorage.getItem(TUTORIAL_STORAGE_KEY);
        if (raw) return new Set(JSON.parse(raw));
    } catch(e) {}
    return new Set();
}

/** Persist a hint ID as seen. */
function _markHintSeen(key) {
    const seen = _getSeenHints();
    seen.add(key);
    try { localStorage.setItem(TUTORIAL_STORAGE_KEY, JSON.stringify([...seen])); } catch(e) {}
}

/**
 * Show a tutorial hint if it hasn't been shown before.
 * @param {string} key - Tip ID (matches L10n key tutorial.{key})
 * @param {boolean} [skipDayGate] - If true, skip Day <= 3 check (for Ink-driven tips)
 */
function showTutorialTip(key, skipDayGate = false) {
    if (!skipDayGate) {
        const day = variableManager.getValue('Day') || 1;
        if (day > 3) return;
    }

    if (_getSeenHints().has(key)) return;

    const text = getTutorialText(key);
    if (!text) return;

    _markHintSeen(key);
    _tipQueue.push({ key, text });

    // Startup grace: defer the first tip of the session until the player
    // has had a moment to read the opening narration.
    const elapsed = Date.now() - SESSION_START;
    if (elapsed < STARTUP_GRACE_MS) {
        setTimeout(_processTipQueue, STARTUP_GRACE_MS - elapsed);
    } else {
        _processTipQueue();
    }
}

function _processTipQueue() {
    if (_tipActive || _tipQueue.length === 0) return;
    // Audit D09: anti-pile-up guard. Don't show a tutorial hint while a
    // higher-priority modal is up — first-run welcome (18+ gate), language
    // picker, onboarding profile picker, save-overwrite confirm, or any
    // open layoutManager overlay. The hint stays in _tipQueue and will be
    // re-attempted on the next call (timed defer below).
    const blockerVisible =
        document.querySelector('#welcome-modal:not(.hidden)') ||
        document.querySelector('#language-popup:not(.hidden)') ||
        document.querySelector('#overlay-onboarding-profile:not(.hidden)') ||
        document.querySelector('.save-confirm-overlay') ||
        document.querySelector('.shop-confirm-overlay');
    if (blockerVisible) {
        setTimeout(_processTipQueue, 1500);
        return;
    }
    _tipActive = true;
    _tipSeenThisSession++;
    const { key, text } = _tipQueue.shift();
    const remaining = _tipQueue.length;

    const anchorSel = TUTORIAL_ANCHORS[key];
    const anchorEl = anchorSel ? document.querySelector(anchorSel) : null;

    // Add pulse highlight to anchor element
    if (anchorEl) anchorEl.classList.add('tutorial-anchor-pulse');

    const tip = document.createElement('div');
    tip.className = 'tutorial-hint';
    if (anchorEl) tip.classList.add('tutorial-hint-anchored');

    // Text body
    const body = document.createElement('span');
    body.className = 'tutorial-text';
    body.textContent = text;
    tip.appendChild(body);

    // Progress counter — only when more tips are queued
    if (remaining > 0) {
        const counter = document.createElement('span');
        counter.className = 'tutorial-counter';
        counter.textContent = `${_tipSeenThisSession}/${_tipSeenThisSession + remaining}`;
        tip.appendChild(counter);
    }

    // "Got it" button
    const dismiss = document.createElement('button');
    dismiss.className = 'tutorial-dismiss';
    dismiss.textContent = L.get('tutorial.dismiss') || 'Got it';
    tip.appendChild(dismiss);

    // "Skip all" — only show when queue has more than one left
    let skipBtn = null;
    if (remaining > 0) {
        skipBtn = document.createElement('button');
        skipBtn.className = 'tutorial-skip-all';
        const skipLabel = L.get('tutorial.skip_all');
        skipBtn.textContent = (skipLabel && skipLabel !== 'tutorial.skip_all') ? skipLabel : 'Skip tips';
        tip.appendChild(skipBtn);
    }

    const cleanup = (skipQueue = false) => {
        if (anchorEl) anchorEl.classList.remove('tutorial-anchor-pulse');
        tip.style.opacity = '0';
        tip.style.transform = tip.style.transform ? tip.style.transform.replace('translateY(0)', 'translateY(-10px)') : 'translateY(-10px)';
        if (skipQueue) {
            // Mark all remaining queued keys as seen so they don't refire
            _tipQueue.forEach(q => _markHintSeen(q.key));
            _tipQueue = [];
        }
        setTimeout(() => {
            tip.remove();
            _tipActive = false;
            // Small pause between tips so the user perceives them as distinct
            if (_tipQueue.length > 0) setTimeout(_processTipQueue, 600);
        }, 400);
    };

    dismiss.addEventListener('click', (e) => { e.stopPropagation(); cleanup(false); });
    if (skipBtn) skipBtn.addEventListener('click', (e) => { e.stopPropagation(); cleanup(true); });
    tip.addEventListener('click', () => cleanup(false));

    document.body.appendChild(tip);

    // Position near anchor if present
    if (anchorEl) {
        _positionNearAnchor(tip, anchorEl);
    }

    // Auto-dismiss after 20s (tutorials need time to read)
    setTimeout(() => { if (tip.parentNode) cleanup(); }, 20000);
}

/** Position tooltip near anchor element, below or above depending on space */
function _positionNearAnchor(tip, anchor) {
    const rect = anchor.getBoundingClientRect();
    const tipH = tip.offsetHeight || 80;
    const spaceBelow = window.innerHeight - rect.bottom;

    // Reset default centering
    tip.style.left = '';
    tip.style.top = '';
    tip.style.transform = '';

    if (spaceBelow > tipH + 20) {
        // Below anchor
        tip.style.top = `${rect.bottom + 8}px`;
        tip.classList.add('tutorial-arrow-up');
    } else {
        // Above anchor
        tip.style.top = `${rect.top - tipH - 8}px`;
        tip.classList.add('tutorial-arrow-down');
    }

    // Horizontal: center on anchor, clamped to viewport
    const centerX = rect.left + rect.width / 2;
    const tipW = tip.offsetWidth || 300;
    const left = Math.max(8, Math.min(centerX - tipW / 2, window.innerWidth - tipW - 8));
    tip.style.left = `${left}px`;

    // Position arrow to point at anchor center (not tooltip center)
    const arrowLeft = Math.max(12, Math.min(centerX - left, tipW - 12));
    tip.style.setProperty('--arrow-left', `${arrowLeft}px`);
}

let _scope = null;

function destroyTutorialTriggers() {
    if (_scope) {
        _scope.destroy();
        _scope = null;
    }
}

// Wire tutorial hint triggers to game events
function bindTutorialTriggers() {
    if (!_scope) _scope = events.createScope('tutorial-tips');
    // Ink #tutorial:key tags (no Day gate — author-controlled placement)
    _scope.on(Events.TUTORIAL_TIP, (key) => {
        showTutorialTip(key, true);
    });

    // 1. First choice with energy cost
    _scope.on(Events.STORY_CHOICES_READY, (choices) => {
        if (!Array.isArray(choices)) return;
        const hasEnergy = choices.some(c => c.energyCost >= 0);
        if (hasEnergy) showTutorialTip('first_energy_choice');
    });

    // 2. First Fem or Sub stat change
    _scope.on(Events.STAT_CHANGED, (name, newVal) => {
        if (name === 'Femininity' || name === 'Submissiveness') {
            showTutorialTip('first_stat_change');
        }
        // 7. First time Arousal > 20
        if (name === 'Arousal' && newVal > 20) {
            showTutorialTip('first_arousal_high');
        }
    });

    // 3. First time opening phone
    _scope.on(Events.OVERLAY_OPEN, (id) => {
        if (id === 'overlay-phone') showTutorialTip('first_phone');
    });

    // 4. First shop visit
    _scope.on(Events.TAG_UI, (mode) => {
        if (mode?.startsWith('shop_')) showTutorialTip('first_shop');
    });

    // 5. First time reaching Night (TimeSlot 3) + 6. Day 2 start
    _scope.on(Events.VARIABLE_CHANGED, (name, value) => {
        if (name === 'TimeSlot' && value >= 3) {
            showTutorialTip('first_night');
        }
        if (name === 'Day' && value === 2) {
            showTutorialTip('day2_routine');
            // P2b (QA-playtest 2026-06-20): week-1 discoverability, every city
            // escalation is Fem-gated while Fem is pacing-locked, so a new player
            // hits a wall of greyed-out choices. The un-gated apartment Computer
            // (streaming/videos) is the intended early arousal engine but was
            // unsignposted until a Day-10 SMS. Point at it on the first free day.
            showTutorialTip('computer_intro');
        }
    });

    // 8. First SMS received
    _scope.on(Events.TAG_SMS, () => {
        showTutorialTip('first_sms');
    });

    // 9. First time a Fem gain is fully suppressed by the pacing phase-lock
    // (early-game setup/half phase). Explains why a feminizing action delivered
    // nothing — the badge is correctly hidden, but silence reads as "broken".
    // skipDayGate: the lock can extend to Day 14/21 on slower pacing modes.
    _scope.on(Events.PACING_GAIN_SUPPRESSED, (mode) => {
        if (mode === 'fem') showTutorialTip('pacing_fem_locked', true);
    });

    // v0.2.6 9. Weekly calendar strip — fire on first STORY_CHOICES_READY
    // of a session. Day-gate (<= 3) means it auto-skips on resumes past
    // Day 3, but new starts will see it on the first beat that surfaces
    // choices. Anchored to the topbar week strip.
    _scope.on(Events.STORY_CHOICES_READY, () => {
        showTutorialTip('weekly_calendar');
    });

    // v0.2.6 10. Endgame Progress panel — fires on first pause-menu open
    // once Day >= 7 (when the panel becomes available). Uses skipDayGate
    // because the trigger condition IS the day gate — without it, the
    // Day <= 3 default would suppress it permanently.
    _scope.on(Events.OVERLAY_OPEN, (id) => {
        if (id === 'overlay-pause') {
            const day = variableManager.getValue('Day') || 1;
            if (day >= 7) showTutorialTip('endgame_progress', true);
        }
    });

    // v0.2.6 11. First achievement unlock — toast shows the achievement
    // but pre-fix the player didn't know they could click it to open the
    // full panel. SkipDayGate because achievements can land any day.
    _scope.on(Events.ACHIEVEMENT_UNLOCKED, () => {
        showTutorialTip('first_achievement', true);
    });

    // v0.2.8 12. Paper-doll preview — first wardrobe open explains the live
    // layered outfit render. SkipDayGate: the feature is new to every player.
    _scope.on(Events.OVERLAY_OPEN, (id) => {
        if (id === 'overlay-wardrobe') showTutorialTip('paperdoll_intro', true);
    });

    // v0.2.8 13. Cloud Save — surfaced on first pause-menu open so players
    // discover the cross-device 6-word-code backup. SkipDayGate.
    _scope.on(Events.OVERLAY_OPEN, (id) => {
        if (id === 'overlay-pause') showTutorialTip('cloud_save', true);
    });

    // v0.3 14. Content Preferences — fires once on LOAD (existing/save-game
    // players skip the character editor where this opt-in normally lives).
    // Gated exactly like the pause section: v030 armed AND the player can see a
    // real track (Patreon for BNWO) — so free players, who never see the toggle,
    // are never nagged. Anchored to the Settings button. SkipDayGate (any day).
    // New-game players are pre-marked seen in character-editor.js.
    //
    // LOAD_COMPLETED fires BEFORE the story is restored into storyManager, so we
    // arm a flag and fire on the first STORY_CHOICES_READY (story guaranteed ready).
    _scope.on(Events.LOAD_COMPLETED, () => { _pendingContentPrefsTip = true; });
    _scope.on(Events.STORY_CHOICES_READY, () => {
        if (!_pendingContentPrefsTip) return;
        _pendingContentPrefsTip = false;
        try {
            const story = storyManager.currentStory;
            if (story && contentMenuEnabled(story) && hasVisibleContentTracks(!isPat())) {
                showTutorialTip('content_prefs', true);
            }
        } catch (e) { /* non-fatal */ }
    });
}

// P0: Select exit choice directly via Story API (for Computer — choices aren't in #choices DOM)
function _selectExitChoiceByStory(keywords) {
    const story = storyManager.currentStory;
    if (!story || !story.currentChoices) return;
    const choices = story.currentChoices;
    // Try each keyword in priority order
    for (const keyword of keywords) {
        for (const c of choices) {
            if (c.text.toLowerCase().includes(keyword)) {
                storyManager.selectChoice(c.index);
                return;
            }
        }
    }
    // Fallback: select last choice
    if (choices.length > 0) {
        storyManager.selectChoice(choices[choices.length - 1].index);
    }
}

// P0: Auto-select exit choice by keyword match (for DOM-based choice buttons)
function autoSelectExitChoice(keywords, retries = 3) {
    // Remove choices-pending blur (planner/overlay may have left it stuck)
    const grid = document.querySelector('.choices-grid');
    if (grid) grid.classList.remove('choices-pending');

    const btns = document.querySelectorAll('#choices .choice-btn:not(.disabled), #choices-inline .choice-btn:not(.disabled), .choice-btn:not(.disabled)');
    for (const keyword of keywords) {
        for (const btn of btns) {
            // Use original English text (data-original) to match, not translated text
            const original = btn.dataset.original || '';
            const display = (btn.querySelector('.choice-text')?.textContent || btn.textContent || '').toLowerCase();
            if (original.includes(keyword) || display.includes(keyword)) {
                btn.click();
                return;
            }
        }
    }
    // Retry after delay — choices might not be rendered yet (overlay animation)
    if (retries > 0 && btns.length === 0) {
        setTimeout(() => autoSelectExitChoice(keywords, retries - 1), 400);
        return;
    }
    // Last resort: click first available choice
    if (btns.length > 0) {
        btns[0].click();
        return;
    }
    console.warn('[PnO] autoSelectExitChoice: no matching choice for:', keywords);
}

export const tutorialTips = {
    show: showTutorialTip,
    markSeen: _markHintSeen,
    bindTriggers: bindTutorialTriggers,
    destroy: destroyTutorialTriggers,
    autoSelectExitChoice,
    _selectExitChoiceByStory,
};
